import React, { useState } from "react";
import { motion } from "framer-motion";

// Simplified stylised regions of India — not a topographically accurate map,
// but an editorial map-of-regions where each blob can be hovered.
const REGIONS = [
    {
        id: "north",
        label: "North",
        color: "var(--tip-saffron)",
        description: "Delhi · Lucknow · Chandigarh",
        d: "M180,40 L380,40 L420,140 L350,220 L200,210 L150,130 Z",
    },
    {
        id: "west",
        label: "West",
        color: "var(--tip-blue)",
        description: "Mumbai · Ahmedabad · Pune",
        d: "M70,180 L200,210 L220,330 L130,420 L60,330 Z",
    },
    {
        id: "south",
        label: "South",
        color: "var(--tip-green)",
        description: "Chennai · Bengaluru · Hyderabad · Kochi",
        d: "M200,360 L330,360 L320,500 L230,560 L150,470 Z",
    },
    {
        id: "east",
        label: "East",
        color: "#0a0a0a",
        description: "Kolkata · Bhubaneswar · Patna",
        d: "M340,210 L460,210 L470,360 L330,360 L320,290 Z",
    },
    {
        id: "northeast",
        label: "North-East",
        color: "var(--tip-saffron)",
        description: "Guwahati · Imphal · Shillong",
        d: "M460,120 L560,110 L570,230 L470,250 L440,180 Z",
    },
];

export const IndiaMap = () => {
    const [active, setActive] = useState("north");
    const region = REGIONS.find((r) => r.id === active) || REGIONS[0];

    return (
        <div
            data-testid="india-map"
            className="relative w-full grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center"
        >
            <div className="relative">
                <svg
                    viewBox="0 0 640 620"
                    className="w-full h-auto max-w-[560px]"
                    role="img"
                    aria-label="Map of India by regions"
                >
                    {/* faint outline */}
                    <rect
                        x="0"
                        y="0"
                        width="640"
                        height="620"
                        fill="transparent"
                    />
                    {REGIONS.map((r) => (
                        <motion.path
                            key={r.id}
                            data-testid={`region-${r.id}`}
                            d={r.d}
                            className="region-path"
                            stroke="#0a0a0a"
                            strokeWidth={1.25}
                            fill={active === r.id ? r.color : "#fafafa"}
                            onMouseEnter={() => setActive(r.id)}
                            onFocus={() => setActive(r.id)}
                            whileHover={{ scale: 1.015 }}
                            style={{ transformOrigin: "center" }}
                        />
                    ))}
                    {REGIONS.map((r) => {
                        // approximate label center
                        const map = {
                            north: [285, 130],
                            west: [135, 305],
                            south: [240, 460],
                            east: [400, 285],
                            northeast: [505, 180],
                        };
                        const [x, y] = map[r.id];
                        return (
                            <text
                                key={`l-${r.id}`}
                                x={x}
                                y={y}
                                textAnchor="middle"
                                className="pointer-events-none"
                                fontFamily="'Cabinet Grotesk', sans-serif"
                                fontWeight={800}
                                fontSize={active === r.id ? 18 : 14}
                                fill={active === r.id ? "#fafafa" : "#0a0a0a"}
                                style={{ transition: "all 240ms ease" }}
                            >
                                {r.label.toUpperCase()}
                            </text>
                        );
                    })}
                </svg>
            </div>

            <div className="border-l border-black/10 pl-8">
                <div className="eyebrow text-black/60 mb-4">
                    Region · {String(REGIONS.findIndex((r) => r.id === active) + 1).padStart(2, "0")} / 05
                </div>
                <motion.h3
                    key={region.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="font-display font-black text-5xl md:text-6xl tracking-tight"
                >
                    {region.label} India.
                </motion.h3>
                <p className="mt-5 text-black/70 max-w-md">
                    {region.description}. Every region of India has a different
                    relationship with progress, language, faith and freedom.
                    We&apos;re here to listen to all of them — without filter.
                </p>

                <div className="mt-8 grid grid-cols-5 gap-2">
                    {REGIONS.map((r) => (
                        <button
                            key={r.id}
                            data-testid={`region-pill-${r.id}`}
                            onClick={() => setActive(r.id)}
                            className={`py-3 text-[10px] font-bold tracking-[0.18em] uppercase border transition-all ${
                                active === r.id
                                    ? "bg-tip-black text-white border-tip-black"
                                    : "bg-white text-tip-black border-black/15 hover:border-tip-black"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
